import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { MailCheck, ArrowLeft } from "lucide-react";

/** Icona occhio ultra-minimale (stroke 1.25, no fill) */
const EyeMini = ({ shown }) => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1.5 10 Q10 3.5 18.5 10 Q10 16.5 1.5 10 Z" />
    {shown ? <circle cx="10" cy="10" r="1.5" /> : <line x1="4" y1="4" x2="16" y2="16" />}
  </svg>
);

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const hasToken = token.length > 0;

  // Step 1: email request
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  // Step 2: new password
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);

  const requestReset = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setEmailSent(true);
    } catch (err) {
      // Backend returns 200 anyway per best practice; ma se c'è errore rete lo mostro
      toast.error(err?.response?.data?.detail || "Errore nell'invio. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("La password deve avere almeno 8 caratteri");
      return;
    }
    if (newPassword !== confirm) {
      toast.error("Le password non coincidono");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      toast.success("Password aggiornata. Accedi con le nuove credenziali.");
      nav("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Link non valido o scaduto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center mb-8" data-testid="reset-brand-link">
          <Logo iconSize={36} textClassName="text-xl" />
        </Link>
        <Card className="p-8 rounded-2xl border shadow-[0_8px_30px_rgb(0,0,0,0.04)]">

          {!hasToken && !emailSent && (
            <>
              <h1 className="font-heading font-bold text-3xl mb-2">Password dimenticata?</h1>
              <p className="text-muted-foreground mb-6 text-sm">
                Inserisci l'email del tuo account. Se esiste, ti inviamo un link per reimpostare la password.
              </p>
              <form onSubmit={requestReset} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@esempio.it" className="h-12 rounded-xl mt-1.5" data-testid="reset-email-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 rounded-full" data-testid="reset-request-submit">
                  {loading ? "Invio in corso..." : "Invia link di reset"}
                </Button>
              </form>
            </>
          )}

          {!hasToken && emailSent && (
            <div className="text-center py-4" data-testid="reset-email-sent">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <MailCheck className="w-8 h-8 text-primary" />
              </div>
              <h1 className="font-heading font-bold text-2xl mb-2">Controlla la tua email</h1>
              <p className="text-muted-foreground text-sm mb-2">
                Se esiste un account con <strong className="text-foreground">{email}</strong>,
                troverai un'email con il link per reimpostare la password.
              </p>
              <p className="text-xs text-muted-foreground mb-6">
                Il link scade dopo 1 ora. Controlla anche la cartella spam.
              </p>
              <Link to="/login" className="inline-flex items-center gap-2 text-sm text-primary font-semibold hover:underline">
                <ArrowLeft className="w-4 h-4" /> Torna al login
              </Link>
            </div>
          )}

          {hasToken && (
            <>
              <h1 className="font-heading font-bold text-3xl mb-2">Nuova password</h1>
              <p className="text-muted-foreground mb-6 text-sm">
                Scegli una nuova password (almeno 8 caratteri).
              </p>
              <form onSubmit={submitNewPassword} className="space-y-4">
                <div>
                  <Label htmlFor="new-password">Nuova password</Label>
                  <div className="relative">
                    <Input id="new-password" type={showPassword ? "text" : "password"} required
                      value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                      className="h-12 rounded-xl mt-1.5 pr-11" data-testid="reset-new-password-input" />
                    <button type="button" onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                      aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                      data-testid="reset-toggle-password">
                      <EyeMini shown={showPassword} />
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm">Conferma password</Label>
                  <Input id="confirm" type={showPassword ? "text" : "password"} required
                    value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    className="h-12 rounded-xl mt-1.5" data-testid="reset-confirm-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 rounded-full" data-testid="reset-submit">
                  {loading ? "Salvataggio..." : "Aggiorna password"}
                </Button>
              </form>
            </>
          )}

          <p className="text-sm text-muted-foreground mt-6 text-center">
            <Link to="/login" className="text-primary font-semibold">Torna al login</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
